import React from 'react';
import { Avatar, Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import {
  UserOutlined, LogoutOutlined, SettingOutlined, IdcardOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { logoutLocal, logoutPassgate } from '@/apis/auth';
import {
  LocalTokens,
  clearAllTokens,
  getCurrentLoginKind,
} from '@/common/auth/tokens';
import { resetCurrentUserCache, useCurrentUser } from '@/common/auth/adminAcl';
import { LOCAL_DEV_AUTH } from '@/common/auth/localDevAuth';
import { getEnvLogo } from '@/common/envLogo';
import {
  WORKSPACES,
  resolveWorkspace,
  getWorkspace,
  type WorkspaceKey,
} from '@/common/workspace';
import './index.less';

const simLogo = getEnvLogo();

/**
 * 全局顶栏：品牌「仿真平台」+ 工作区入口（SIL / HIL）+ 用户区。
 *
 * 工作区是全局维度（切换 = 换整条左栏），所以放在横跨整宽的深色顶栏里、
 * 紧跟品牌名，读作「仿真平台 › SIL / HIL」，而不是某个页面内的功能分页。
 * 当前工作区由 pathname 反推（见 common/workspace.ts），不存独立状态。
 */
const WorkspaceNav: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const current: WorkspaceKey = resolveWorkspace(location.pathname);

  return (
    <nav className="workspace-nav" aria-label="工作区">
      {WORKSPACES.map((ws) => {
        const active = ws.key === current;
        return (
          <button
            key={ws.key}
            type="button"
            title={ws.hint}
            aria-current={active ? 'page' : undefined}
            className={`workspace-nav-item ${active ? 'workspace-nav-item--active' : ''}`}
            onClick={() => {
              // 已在该工作区内再点自己时不动，避免把用户从子页面拽回默认页。
              if (!active) navigate(getWorkspace(ws.key).defaultPath);
            }}
          >
            {ws.label}
          </button>
        );
      })}
    </nav>
  );
};

const AppHeader: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { me, isAdmin } = useCurrentUser();

  const handleLogout = async () => {
    if (LOCAL_DEV_AUTH) return;
    const kind = getCurrentLoginKind();
    if (kind === 'local') {
      await logoutLocal(LocalTokens.getRefresh());
    } else if (kind === 'passgate') {
      await logoutPassgate();
    }
    clearAllTokens();
    resetCurrentUserCache();
    navigate('/user-login', { replace: true });
  };

  const dropdownItems: MenuProps['items'] = [
    {
      key: 'account',
      icon: <IdcardOutlined />,
      label: '个人设置',
      onClick: () => navigate('/account-settings'),
    },
    ...(isAdmin
      ? [
          {
            key: 'admin',
            icon: <SettingOutlined />,
            label: '后台管理',
            onClick: () => navigate('/admin'),
          },
        ]
      : []),
    { type: 'divider' as const },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      disabled: LOCAL_DEV_AUTH,
      danger: true,
      onClick: handleLogout,
    },
  ];

  const displayName = me?.user?.name || '用户';
  const avatarUrl = me?.user?.avatarUrl;

  return (
    <header className="app-header">
      <div className="app-header-left">
        <button
          type="button"
          className="app-header-brand"
          onClick={() => navigate(getWorkspace(resolveWorkspace(location.pathname)).defaultPath)}
        >
          <img className="app-header-logo" src={simLogo} alt="" />
          <span className="app-header-title">仿真平台</span>
        </button>
        <span className="app-header-divider" aria-hidden />
        <WorkspaceNav />
      </div>
      <div className="app-header-right">
        <Dropdown menu={{ items: dropdownItems }} placement="bottomRight" trigger={['click']}>
          <div className="app-header-user">
            <Avatar
              size={28}
              src={avatarUrl || undefined}
              icon={!avatarUrl ? <UserOutlined /> : undefined}
              className="app-header-avatar"
            />
            <span className="app-header-username">{displayName}</span>
          </div>
        </Dropdown>
      </div>
    </header>
  );
};

export default AppHeader;
